import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type { AiKissQualityId, AiKissTemplateId } from "./ai-kiss-preset";

/**
 * /ai-kiss-video-generator 样片区的片子。
 *
 * Examples 优先用竞品样片（2026-10-12 规矩）：4 条外部片 onSite false，不进 VideoObject，
 * 卡上不标来源和模型，来源只记在这里和 packages/scripts/ai-kiss-showcase/README.md。
 * 自产片在本页 hero 用预设出，onSite true，标档位，「Try this example」回填原图。
 *
 * R2 `home/showcase/ai-kiss/` 下 clips/{id}.mp4、posters/{id}.jpg、refs/{id}-1.jpg。
 * 清单为空时样片区整个不渲染。
 */
export interface AiKissShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 出这条片用的画质档（只有自产片有），拼贴 hover 时显示档位名 */
	quality?: AiKissQualityId;
	/** 出这条片用的模板；「Try this example」顺带切过去 */
	template?: AiKissTemplateId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 原图（合照一张，或两张单人照），「Try this example」填进 hero 的槽 */
	inputImages?: string[];
	/** 外部片的来源（只记档案，页面上不显示） */
	source?: string;
}

const R2_BASE = "home/showcase/ai-kiss";

/**
 * 视频和 poster 地址的版本参数。assets 域的 GET 走 Cloudflare 边缘缓存（max-age 一天），同一个 key
 * 覆盖上传后一天内还是旧文件，上传前被请求过的连 404 也会缓存一天（10/12 两张 poster 就这样空白）；
 * 换片子时把这个数字加一
 */
const CLIP_VERSION = 1;

type Spec = Omit<AiKissShowcaseClip, "videoUrl" | "poster" | "inputImages"> & {
	/** 自产片的原图 id 列表（refs/{ref}.jpg） */
	refs?: string[];
};

/**
 * 顺序即拼贴顺序（一行排开，从左到右；手机上前两条半在第一屏）。两条本页 HD 自产片排第 1、4 条；
 * 外部片 2026-10-12 抓取，来源：imgveo.com/ai-kissing-generator 的 kiss-couple；
 * magicshot.ai/ai-video-generator/kissing-booth 的 Rain / Euro Summer / Paris Balcony 风格卡
 */
const SPECS: Spec[] = [
	{
		id: "sweet-couple",
		title: "Sweet Kiss from One Couple Photo",
		description:
			"From one couple photo: they turn to each other, lean in and share a gentle kiss, his hand on her cheek.",
		quality: "hd",
		template: "sweet",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["couple-1"],
	},
	{
		id: "ext-rain",
		title: "Kiss in the Rain",
		description:
			"A couple kisses under night rain, her hand on his cheek, drops catching the light.",
		orientation: "portrait",
		onSite: false,
		duration: 8,
		hasAudio: false,
		source: "magicshot.ai (Rain)",
	},
	{
		id: "ext-couple",
		title: "Sweet Kiss at Home",
		description:
			"A couple leans in against a plain wall and shares a slow, gentle kiss.",
		orientation: "portrait",
		onSite: false,
		duration: 5,
		hasAudio: false,
		source: "imgveo.com (kiss-couple)",
	},
	{
		id: "french-two",
		title: "French Kiss from Two Selfies",
		description:
			"From two separate photos: the two people meet face to face and share a slow, passionate kiss, her hand in his hair.",
		quality: "hd",
		template: "french",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["two-1", "two-2"],
	},
	// 11/01 补：右栏样片跟 tab 走，Forehead kiss 原来没有自己的片（加了一句 Director's note 才亲在额头上）
	{
		id: "forehead-couple",
		title: "Forehead Kiss from One Couple Photo",
		description:
			"From one couple photo: he leans down and kisses the center of her forehead as she closes her eyes and smiles, then they rest forehead to forehead.",
		quality: "hd",
		template: "forehead",
		orientation: "portrait",
		onSite: true,
		duration: 5,
		hasAudio: true,
		refs: ["couple-1"],
	},
	{
		id: "ext-euro-summer",
		title: "Euro Summer Kiss",
		description:
			"White walls and bougainvillea: a couple kisses on a sunny Mediterranean street.",
		orientation: "portrait",
		onSite: false,
		duration: 8,
		hasAudio: false,
		source: "magicshot.ai (Euro Summer)",
	},
	{
		id: "ext-paris-balcony",
		title: "Paris Balcony Kiss",
		description:
			"A couple kisses on a Paris balcony at night with the city lights behind them.",
		orientation: "portrait",
		onSite: false,
		duration: 5,
		hasAudio: false,
		source: "magicshot.ai (Paris Balcony)",
	},
];

export const aiKissShowcaseClips: AiKissShowcaseClip[] = SPECS.map(
	({ refs, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(
			`${R2_BASE}/posters/${spec.id}.jpg?v=${CLIP_VERSION}`,
		),
		inputImages: refs?.map((ref) =>
			buildR2AssetsImageUrl(`${R2_BASE}/refs/${ref}.jpg`),
		),
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const AI_KISS_SHOWCASE_UPLOADED_AT = "2026-10-12T00:00:00+08:00";
